import React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, Theme } from '../../packages/constants';
import { AutoCompleteTextInput, DropDownAction } from '../AutoCompleteTextInput';
import { Button } from '../button';

export interface AutoCompleteTextFieldProps {
    name: string;
    dropdownOnChange: (value: string, command: DropDownAction) => void;
    defaultItems?: string[];
    defaultPlaceholder?: string;
    ariaLabel?: string;
    buttonName?: string;
    dataId?: string;
    theme?: Theme;
    label?: string;
    thinLabel?: boolean;
    disabled?: boolean;
    invalid?: boolean;
    required?: boolean;
    lineHeight?: number;
    fontSize?: number;
    disabledButtonHandler?: (value: string) => boolean;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLabel?: boolean;
    lineHeight?: number;
    fontSize?: number;
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
`;

const WrapperBtn = styled.div`
    display: block;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    font-size: ${(props: StyledTextLabelProps): string => (props.fontSize ? `${props.fontSize}px` : FONT_SIZE.SMALL)};
    line-height: ${(props: StyledTextLabelProps): string =>
        props.lineHeight ? `${props.lineHeight}px` : FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

export const AutoCompleteTextField = ({
    dropdownOnChange,
    defaultItems = [],
    buttonName = 'Submit',
    defaultPlaceholder = 'Please enter a value',
    ariaLabel = 'Autocomplete input',
    label,
    dataId,
    theme,
    invalid,
    disabled,
    thinLabel,
    name,
    required = false,
    lineHeight,
    fontSize,
    disabledButtonHandler,
}: AutoCompleteTextFieldProps) => {
    const [value, setValue] = React.useState('');

    return (
        <Wrapper data-test-id={`${dataId}`}>
            {label ? (
                <StyledTextLabel
                    invalid={invalid}
                    disabled={disabled}
                    thinLabel={thinLabel}
                    lineHeight={lineHeight}
                    fontSize={fontSize}
                >
                    {label}
                </StyledTextLabel>
            ) : (
                ''
            )}
            <AutoCompleteTextInput
                defaultItems={defaultItems}
                defaultPlaceholder={defaultPlaceholder}
                textValue={value}
                textOnChange={setValue}
                dropdownOnChange={dropdownOnChange}
                aria-label={ariaLabel}
                dataId={`${dataId}__input`}
                disabled={disabled}
                invalid={invalid}
                name={name}
                required={required}
                lineHeight={lineHeight}
                fontSize={fontSize}
            />
            <WrapperBtn>
                <Button
                    dataId={buttonName}
                    onClick={() => dropdownOnChange(value, DropDownAction.Insert)}
                    disabled={disabledButtonHandler ? disabledButtonHandler(value) : !value}
                    size={`${fontSize}px`}
                    theme={theme}
                >
                    {buttonName}
                </Button>
            </WrapperBtn>
        </Wrapper>
    );
};

AutoCompleteTextField.displayName = 'AutoCompleteTextField';
